import DatePicker from '@/components/DatePicker';
import Layout from '@/components/layout';
import { getSomeDaysAgo } from '@/helpers/time';
import { useCustomUserIdClarity } from '@/hooks/useCustomUserIdClarity';
import { useDataAverageAnalytic } from '@/hooks/useDataAverageAnalytic';
import useUserPlan from '@/hooks/useUserPlan';
import AverageRate, { IDataAverage } from '@/pages/Analytics/components/AverageRate';
import ByRules from '@/pages/Analytics/components/ByRules';
import OverTime from '@/pages/Analytics/components/OverTime';
import { mockDataAnalytics, mockDataProductAnalytic } from '@/pages/Analytics/sampleData';
import { AnalyticsStyled } from '@/pages/Analytics/styled';
import { apiCaller } from '@/redux/query';
import bannerSlice, { bannerSelector } from '@/redux/slice/banner.slice';
import { Banner, Button } from '@shopify/polaris';
import { RefreshIcon } from '@shopify/polaris-icons';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface IFilters {
  startDate: Date;
  endDate: Date;
}

const now = new Date();

const initFilters = {
  startDate: getSomeDaysAgo(7),
  endDate: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59),
};

const Analytics = () => {
  useCustomUserIdClarity('Analytics');
  const dispatch = useDispatch();
  const { isVisibleBannerLimitAnalyticForStandard: isVisibleBannerLimit } = useSelector(bannerSelector);
  const [titleDatePicker, setTitleDatePicker] = useState('Last 7 days');
  const [filters, setFilters] = useState(initFilters);
  const [refreshKey, setRefreshKey] = useState(0);
  const { isFreePlan, isStandardPlan } = useUserPlan();
  const { data: dataRes } = apiCaller.useGetDataAnalyticQuery(
    {
      startDate: filters.startDate.toISOString(),
      endDate: filters.endDate.toISOString(),
      refetch: refreshKey,
    },
    { skip: isFreePlan },
  );
  const { data: productAnalyticRes } = apiCaller.useGetDataProductAnalyticQuery(undefined, { skip: isFreePlan });

  const data = useMemo(() => {
    return isFreePlan ? mockDataAnalytics : dataRes?.data || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(dataRes), isFreePlan]);

  const dataProductAnalytic = useMemo(() => {
    return isFreePlan ? mockDataProductAnalytic : productAnalyticRes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(productAnalyticRes), isFreePlan]);

  const handleUpdateFilters = (data: Partial<IFilters>) => {
    setFilters((prev) => ({ ...prev, ...data }));
  };

  const dataAverageRate: IDataAverage = useDataAverageAnalytic(
    data,
    dataProductAnalytic?.appliedProducts,
    dataProductAnalytic?.totalActiveRules,
  );

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1); // Tăng refreshKey để gọi lại API
  };

  return (
    <AnalyticsStyled>
      <Layout title="Analytics" fullWidth={false} subtitle="Measure the efficiency of ETA banners">
        {isVisibleBannerLimit ? (
          isFreePlan ? (
            <div className="mb-16">
              <Banner
                tone="info"
                //@ts-ignore-next-line
                // action={{ content: 'Upgrade now', onAction: () => navigate(PATH.PRICING_PLAN), variant: 'primary' }}
                onDismiss={() =>
                  dispatch(
                    bannerSlice.actions.handleVisibleBanner({ key: 'isVisibleBannerLimitAnalyticForStandard', value: false }),
                  )
                }
              >
                This is sample data for Analytics. Upgrade now to unlock full access and measure performance of ETA banners.
              </Banner>
            </div>
          ) : isStandardPlan ? (
            <div className="mb-16">
              <Banner
                tone="info"
                onDismiss={() =>
                  dispatch(
                    bannerSlice.actions.handleVisibleBanner({ key: 'isVisibleBannerLimitAnalyticForStandard', value: false }),
                  )
                }
                //@ts-ignore-next-line
                // action={{ content: 'Upgrade now', onAction: () => navigate(PATH.PRICING_PLAN), variant: 'primary' }}
              >
                Standard Plan only allows you to view data from the last 7 days. Upgrade now to access comprehensive data for all
                time.
              </Banner>
            </div>
          ) : null
        ) : null}

        <div className="d-flex gap-8">
          <Button icon={RefreshIcon} disabled={isFreePlan} onClick={handleRefresh}>
            Refresh
          </Button>
          <DatePicker
            titleButton={titleDatePicker}
            setTitleButton={(value) => setTitleDatePicker(value)}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onSave={(start, end) => {
              handleUpdateFilters({
                startDate: start,
                endDate: end,
              });
            }}
            hideCustomBtn={true}
            disabled={isFreePlan}
            onlySelectLast7Days={isStandardPlan}
          />
        </div>
        <div className="mt-16">
          <AverageRate dataAverageRate={dataAverageRate} />
        </div>
        <div className="mt-16">
          <OverTime data={data} startDate={filters.startDate} endDate={filters.endDate} />
        </div>

        <div className="mt-16">
          <ByRules dataAnalytic={data} productAnalytic={dataProductAnalytic?.appliedProducts || []} />
        </div>
      </Layout>
    </AnalyticsStyled>
  );
};

export default Analytics;
